import React, { useEffect, useState } from "react";
import {
  CRow,
  CCol,
  CProgress,
  CCard,
  CCardBody,
  CCardHeader,
  CSelect,
  CFormGroup,
  CLabel,
} from "@coreui/react";

import { getItem } from "../api/generics";
import { RootState } from "../store";
import { useSelector } from "react-redux";

const periodOptions = [
  { value: "day", label: "Día" },
  { value: "week", label: "Semana" },
  { value: "month", label: "Mes" },
];

const Dashboard2Page: React.FC = () => {
  const taxPayer = useSelector(
    (state: RootState) => state.taxPayer.data.taxPayer,
  );
  const [period, setPeriod] = useState<string>("month");
  const [stats, setStats] = useState<any>(null);
  const [allowance, setAllowance] = useState<any>(null);

  const loadStats = async (p: string) => {
    if (!taxPayer) return;
    const params = new Map<string, string | undefined>();
    params.set("tax_payer_id", String(taxPayer.id));
    params.set("period", p);
    const resp = await getItem<any>("/stats/documents/", params);
    if (resp.status === "success" || resp.status === undefined) {
      setStats(resp.data || resp);
    } else {
      setStats(null);
    }

    const resp2 = await getItem<any>(
      "/stats/allowance/",
      new Map<string, string | undefined>([
        ["tax_payer_id", String(taxPayer.id)],
      ]),
    );
    if (resp2.status === "success" || resp2.status === undefined) {
      setAllowance(resp2.data || resp2);
    } else {
      setAllowance(null);
    }
  };

  useEffect(() => {
    loadStats(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxPayer, period]);

  const calculatePercent = (
    used: number | undefined,
    total: number | undefined,
  ) => {
    if (!used || !total || total === 0) return 0;
    return Math.min((used / total) * 100, 100);
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return "danger";
    if (percentage >= 70) return "warning";
    return "success";
  };

  return (
    <div>
      <CCard>
        <CCardBody>
          <h1 style={{ marginLeft: "15px" }}>
            Estadísticas de uso, {taxPayer.socialName}
          </h1>
        </CCardBody>
      </CCard>
      <CCard>
        <CCardHeader>
          <strong>Estadísticas de Documentos</strong>
          <div className="card-header-actions">
            <CFormGroup row className="mb-0">
              <CCol xs="12" md="auto">
                <CLabel htmlFor="period-select" className="mr-2">
                  Periodo:
                </CLabel>
              </CCol>
              <CCol xs="12" md="auto">
                <CSelect
                  id="period-select"
                  value={period}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setPeriod(e.target.value)
                  }
                  size="sm"
                >
                  {periodOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </CSelect>
              </CCol>
            </CFormGroup>
          </div>
        </CCardHeader>
        <CCardBody>
          {stats ? (
            <>
              <CRow>
                <CCol sm="6" lg="4">
                  <CCard className="text-white bg-primary">
                    <CCardBody className="d-flex align-items-center justify-content-between">
                      <div>
                        <div className="h4 mb-0">
                          {String(stats.counts?.invoices || 0)}
                        </div>
                        <div className="small">Facturas</div>
                      </div>
                    </CCardBody>
                  </CCard>
                </CCol>

                <CCol sm="6" lg="4">
                  <CCard className="text-white bg-info">
                    <CCardBody>
                      <div className="h4 mb-0">
                        {String(stats.counts?.credit_notes || 0)}
                      </div>
                      <div className="small">Notas de Crédito</div>
                    </CCardBody>
                  </CCard>
                </CCol>

                <CCol sm="6" lg="4">
                  <CCard className="text-dark bg-warning">
                    <CCardBody>
                      <div className="h4 mb-0">
                        {String(stats.counts?.debit_notes || 0)}
                      </div>
                      <div className="small">Notas de Débito</div>
                    </CCardBody>
                  </CCard>
                </CCol>

                <CCol sm="6" lg="4">
                  <CCard className="text-white bg-success">
                    <CCardBody>
                      <div className="h4 mb-0">
                        {String(stats.counts?.remission_notes || 0)}
                      </div>
                      <div className="small">Notas de Remisión</div>
                    </CCardBody>
                  </CCard>
                </CCol>

                <CCol sm="6" lg="4">
                  <CCard className="text-white bg-dark">
                    <CCardBody>
                      <div className="h4 mb-0">
                        {String(stats.counts?.auto_invoices || 0)}
                      </div>
                      <div className="small">Autofacturas</div>
                    </CCardBody>
                  </CCard>
                </CCol>

                <CCol sm="6" lg="4">
                  <CCard className="text-white bg-danger">
                    <CCardBody>
                      <div className="h4 mb-0">
                        {String(stats.counts?.receipts || 0)}
                      </div>
                      <div className="small">Recibos</div>
                    </CCardBody>
                  </CCard>
                </CCol>
              </CRow>

              <CRow className="mt-4">
                <CCol>
                  <CCard className="bg-primary text-white">
                    <CCardBody>
                      <h5 className="mb-0">
                        Total de Documentos:{" "}
                        <strong>{stats.totalCount || 0}</strong>
                      </h5>
                    </CCardBody>
                  </CCard>
                </CCol>
              </CRow>
            </>
          ) : (
            <div className="text-center text-muted py-4">
              Cargando estadísticas...
            </div>
          )}

          <CRow className="mt-4">
            <CCol>
              <CCard>
                <CCardHeader>
                  <strong>Uso de Cuota</strong>
                  {allowance?.period && (
                    <span className="text-muted ml-2">
                      (
                      {allowance.period === "monthly"
                        ? "Mensual"
                        : allowance.period === "yearly"
                          ? "Anual"
                          : allowance.period}
                      )
                    </span>
                  )}
                </CCardHeader>
                <CCardBody>
                  {allowance ? (
                    allowance.hasLimit ? (
                      <>
                        <div className="mb-3">
                          <strong>
                            {allowance.receiptsUseDesCounter
                              ? "DEs y recibos: "
                              : "Documentos (DES): "}
                          </strong>
                          {typeof allowance.usedDes !== "undefined"
                            ? allowance.usedDes
                            : (stats?.totalDesCount ?? 0)}
                          <strong> / </strong>
                          {allowance.totalLimitDes ??
                            allowance.limitDes ??
                            allowance.allowance ??
                            "-"}
                        </div>
                        <CProgress
                          className="mb-2"
                          color={getProgressColor(
                            calculatePercent(
                              typeof allowance.usedDes !== "undefined"
                                ? allowance.usedDes
                                : (stats?.totalDesCount ?? 0),
                              allowance.totalLimitDes ??
                                allowance.limitDes ??
                                undefined,
                            ),
                          )}
                          value={calculatePercent(
                            typeof allowance.usedDes !== "undefined"
                              ? allowance.usedDes
                              : (stats?.totalDesCount ?? 0),
                            allowance.totalLimitDes ??
                              allowance.limitDes ??
                              undefined,
                          )}
                          animated
                        />
                        <small className="text-muted mb-3 d-block">
                          {calculatePercent(
                            typeof allowance.usedDes !== "undefined"
                              ? allowance.usedDes
                              : (stats?.totalDesCount ?? 0),
                            allowance.totalLimitDes ??
                              allowance.limitDes ??
                              undefined,
                          ).toFixed(1)}
                          % utilizado
                          {allowance.extraDes > 0 && (
                            <> · Incluye {allowance.extraDes} extra(s) concedidos</>
                          )}
                        </small>

                        {!allowance.receiptsUseDesCounter && (
                          <>
                            <div className="mb-3">
                              <strong>Recibos: </strong>
                              {typeof allowance.usedReceipts !== "undefined"
                                ? allowance.usedReceipts
                                : (stats?.counts?.receipts ?? 0)}
                              <strong> / </strong>
                              {allowance.totalLimitReceipts ??
                                allowance.limitReceipts ??
                                "-"}
                            </div>
                            <CProgress
                              className="mb-2"
                              color={getProgressColor(
                                calculatePercent(
                                  typeof allowance.usedReceipts !== "undefined"
                                    ? allowance.usedReceipts
                                    : (stats?.counts?.receipts ?? 0),
                                  allowance.totalLimitReceipts ??
                                    allowance.limitReceipts ??
                                    undefined,
                                ),
                              )}
                              value={calculatePercent(
                                typeof allowance.usedReceipts !== "undefined"
                                  ? allowance.usedReceipts
                                  : (stats?.counts?.receipts ?? 0),
                                allowance.totalLimitReceipts ??
                                  allowance.limitReceipts ??
                                  undefined,
                              )}
                              animated
                            />
                            <small className="text-muted d-block">
                              {calculatePercent(
                                typeof allowance.usedReceipts !== "undefined"
                                  ? allowance.usedReceipts
                                  : (stats?.counts?.receipts ?? 0),
                                allowance.totalLimitReceipts ??
                                  allowance.limitReceipts ??
                                  undefined,
                              ).toFixed(1)}
                              % utilizado
                              {allowance.extraReceipts > 0 && (
                                <>
                                  {" "}· Incluye {allowance.extraReceipts}{" "}
                                  extra(s) concedidos
                                </>
                              )}
                            </small>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="mb-2">
                          <strong>Documentos (DES) total: </strong>
                          {stats?.totalDesCount ?? stats?.totalCount ?? 0}
                        </div>
                        <div>
                          <strong>Recibos total: </strong>
                          {stats?.counts?.receipts ?? 0}
                        </div>
                      </>
                    )
                  ) : (
                    <div className="text-muted">Cargando cuota...</div>
                  )}
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </div>
  );
};

export default Dashboard2Page;
